"use client";

import Image from "next/image";
import { useRouter } from "next/navigation";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { Department, Global, NotFoundScreen } from "../../lib/directus-schema";
import { mapCmsButton } from "@/lib/cmsButton";
import { fillPlaceholders } from "@/lib/placeholders";
import { getDirecutsImageUrl } from "@/app/utils/getDirectusImageUrl";
import MarkdownText from "../ui/texts/MarkdownText";
import store from "@/store/store";
import { ButtonTypes, LogoSizes } from "@/types/enums";
import Footer from "../ui/texts/Footer";
import Logo from "../ui/Logo";
import Dropdown from "../ui/Dropdown";
import Button from "../ui/buttons/Button";

type NotFoundMode = "generic" | "customer_not_found" | "department_not_found";

type DepartmentOption = Pick<Department, "id" | "name" | "slug">;

type Props = {
    initialGlobal: Global | null;
    data?: NotFoundScreen | null;
    mode?: NotFoundMode;
    message?: string;
    customerSlug?: string;
    departments?: DepartmentOption[];
};

const NotFoundClient = observer(function NotFoundClient({
    initialGlobal,
    data,
    mode = "generic",
    message,
    customerSlug,
    departments = [],
}: Props) {
    const router = useRouter();
    const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);

    useEffect(() => {
        store.globalData = initialGlobal;
    }, [initialGlobal]);

    const departmentOptions = useMemo(
        () =>
            departments
                .filter((department) => typeof department.id === "number" && Boolean(department.slug))
                .map((department) => ({
                    label: department.name,
                    value: department.id,
                })),
        [departments]
    );

    const tr = data?.translations?.[0] ?? null;
    const backToTeam = mapCmsButton(tr?.back_to_team);
    const imageSrc = tr?.not_found_image ? getDirecutsImageUrl(tr.not_found_image.toString()) : "/images/404.png";

    const singleDepartment =
        departments.length === 1 && departments[0]?.slug ? departments[0] : null;

    const defaultMessage =
        mode === "customer_not_found"
            ? "Kunde nicht gefunden."
            : mode === "department_not_found"
                ? "Diese Abteilung wurde nicht gefunden. Wähle deine Abteilung und spiel weiter."
                : tr?.description || "Diese URL ist nicht im Dienst.";

    const hasSingleDepartmentAction = mode === "department_not_found" && Boolean(singleDepartment && customerSlug);
    const hasDropdownAction = mode === "department_not_found" && !singleDepartment && departmentOptions.length > 0;
    const hasActions = hasSingleDepartmentAction || hasDropdownAction;
    const hasMessage = Boolean(message) || mode !== "generic";

    const handleDepartmentChange = (departmentId: number) => {
        if (!customerSlug) return;

        const selectedDepartment = departments.find((department) => department.id === departmentId);
        if (!selectedDepartment?.slug) return;

        setSelectedDepartmentId(departmentId);
        store.setSelectedDepartment(selectedDepartment.slug);
        router.push(`/${customerSlug}/${selectedDepartment.slug}`);
    };

    const handleSingleDepartmentRedirect = () => {
        if (!customerSlug || !singleDepartment?.slug) return;
        store.setSelectedDepartment(singleDepartment.slug);
        router.push(`/${customerSlug}/${singleDepartment.slug}`);
    };

    return (
        <div className="fixed inset-0 flex flex-col justify-between items-center px-4 text-center">
            <div className="flex flex-col justify-center items-center gap-2.5 px-4 pt-8 w-full">
                <Logo size={LogoSizes.MEDIUM} />
                <div className="w-71.25 h-45">
                    <Image
                        src={imageSrc}
                        alt="404"
                        width={300}
                        height={180}
                        className="object-cover"
                    />
                </div>
                {hasMessage || hasActions ? (
                    <div className="flex flex-col gap-3 w-full max-w-150 h-50">
                        {hasMessage ? (
                            <MarkdownText className="font-20-bold text-green-800" content={message ?? defaultMessage} />
                        ) : null}

                        {mode === "department_not_found" && singleDepartment && customerSlug ? (
                            <Button
                                variant={ButtonTypes.COLOR}
                                onClick={handleSingleDepartmentRedirect}
                                iconRight={backToTeam?.iconRight}
                                subtitle={backToTeam?.subtitle}
                            >
                                {backToTeam?.label
                                    ? fillPlaceholders(backToTeam.label, { department: singleDepartment.name ?? "" })
                                    : `Zur Abteilung ${singleDepartment.name}`}
                            </Button>
                        ) : null}

                        {mode === "department_not_found" && !singleDepartment && departmentOptions.length > 0 ? (
                            <Dropdown
                                openTop={false}
                                placeholder={tr?.department_dropdown_placeholder || "Wähle deine Abteilung"}
                                options={departmentOptions}
                                value={selectedDepartmentId}
                                onChange={handleDepartmentChange}
                            />
                        ) : null}
                    </div>
                ) : null}
            </div>

            <div className="flex flex-col justify-center items-center gap-2.5 px-4 w-full max-w-150">
                <Footer />
            </div>
        </div>
    );
});

export default NotFoundClient;
